import Anchor from "../glide_modules/anchor"
import Glide, { Controls, Breakpoints, Swipe, Autoplay, Images  } from '@glidejs/glide/dist/glide.modular.esm'
import SliderTransformator from "./SliderTransformator";
import SliderOptions from "./SliderOptions";
import ElementSize from "../utils/ElementSize";
import { icon, swapIcon } from "../../../common/js/icon";
import { prefersReducedMotion } from "../../../common/js/motion";

/**
 * Only the slide in view can be reached: every other slide and every clone of the carousel is
 * inert, so Tab does not walk into images out of view (where the browser scrolled the track and
 * broke its transform) and a screen reader reads every image once. Until 7.0.0 20 of the 26
 * focusable links of a slider of an article were out of view (A11Y-8).
 *
 * @param {Element} root the .glide element
 */
export function markSlidesOutOfView(root) {
    root.querySelectorAll('.glide__slide').forEach((slide) => {
        const inView = slide.classList.contains('glide__slide--active') && !slide.classList.contains('glide__slide--clone');
        slide.toggleAttribute('inert', !inView);
    });
}

/**
 * The pause button of a slider which plays by itself (WCAG 2.2.2: what moves by itself for more
 * than five seconds can be paused): an icon button beside the arrows, named by the words of the
 * slide show of the lightbox, which pauses the autoplay for good and starts it again. Glide's
 * own pause on hover stays; a paused slider no longer starts again when the pointer leaves.
 *
 * @param {Element} root the .glide element
 * @param {{pause: Function, play: Function}} glide
 * @param {boolean} [paused] whether the slider is paused already (one built again after a resize)
 * @param {(paused: boolean) => void} [changed] told when the visitor pauses or plays
 * @returns {HTMLButtonElement}
 */
export function addPauseButton(root, glide, paused = false, changed = () => {}) {
    const texts = typeof window !== 'undefined' ? window.EventGalleryLightboxConfiguration : null;
    const names = {
        pause: (texts && texts.KEY_PAUSESLIDESHOW) || 'Pause slide show',
        play: (texts && texts.KEY_PLAYSLIDESHOW) || 'Play slide show',
    };
    let controls = root.querySelector('.glide__arrows');
    if (!controls) {
        controls = document.createElement('div');
        controls.className = 'glide__arrows';
        root.appendChild(controls);
    }
    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'glide__pause eg-btn eg-btn--secondary eg-btn--icon';
    button.innerHTML = icon('pause');
    let playing = !paused;
    const show = () => {
        button.setAttribute('aria-label', playing ? names.pause : names.play);
        button.title = playing ? names.pause : names.play;
        swapIcon(button.querySelector('svg'), playing ? 'pause' : 'play');
    };
    button.addEventListener('click', () => {
        playing = !playing;
        if (playing) {
            glide.play();
        } else {
            glide.pause();
        }
        show();
        changed(!playing);
    });
    show();
    controls.appendChild(button);
    return button;
}

/** the attributes an image of a slider waits in while Glide clones it (withImagesHeld()) */
const HELD = 'data-eg-held-';

/**
 * Runs what makes the clones of a carousel - glide.mount() - while the images of the slider have
 * no address. Glide's carousel clones its first and its last slides (Clones.collect()) before it
 * puts the clones into the page, and a browser loads the image of an element which is not in the
 * page at once, whatever its loading="lazy" says: a slider far down a page - the slides of the
 * event module, a slider of an article - loaded the images of its clones while the page was
 * parsed, on a phone at a pixel ratio of 3 the 1600 px files nobody saw. The addresses wait in
 * data attributes and come back once the clones are in the page, where loading="lazy" holds
 * them back like every other image of the page. src goes first and comes back last, so that the
 * srcset decides as before and the fallback address of src is never asked for on its own.
 *
 * @param {Element} root the .glide element
 * @param {Function} callback
 * @returns {*} what the callback returns
 */
export function withImagesHeld(root, callback) {
    root.querySelectorAll('img').forEach((img) => {
        ['src', 'srcset'].forEach((name) => {
            if (img.hasAttribute(name)) {
                img.setAttribute(HELD + name, img.getAttribute(name));
                img.removeAttribute(name);
            }
        });
    });

    try {
        return callback();
    } finally {
        // the clones carry the held addresses as well: they were made from the slides
        root.querySelectorAll('img').forEach((img) => {
            ['srcset', 'src'].forEach((name) => {
                if (img.hasAttribute(HELD + name)) {
                    img.setAttribute(name, img.getAttribute(HELD + name));
                    img.removeAttribute(HELD + name);
                }
            });
        });
    }
}

/** how far above or below the window a slider starts to load the images of all its slides */
const NEAR = '1250px 0px';

/**
 * The images of a slider are lazy like every image of a module, so a slider far down a page loads
 * nothing while the page is parsed. But a lazy image the slider keeps out of its track is clipped
 * and would load only when it slides in, so once the slider comes near the window every image of
 * it - the hidden slides and the clones - is asked for at once (loading="eager"), and a slide is
 * there when the slider shows it. Until 7.0.0 the slide layout of the event module printed every
 * image eager, which loaded them while the page was parsed wherever the module stood.
 *
 * @param {Element} root the .glide element
 * @returns {IntersectionObserver|null} the observer, null when the images were asked for at once
 */
export function loadSlidesWhenNear(root) {
    const loadAll = () => root.querySelectorAll('img[loading="lazy"]').forEach((img) => img.setAttribute('loading', 'eager'));

    if (typeof IntersectionObserver === 'undefined') {
        loadAll();
        return null;
    }

    const observer = new IntersectionObserver((entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
            observer.disconnect();
            loadAll();
        }
    }, { rootMargin: NEAR });
    observer.observe(root);

    return observer;
}

export default class SliderStarter {

    constructor(sourceGroupElement) {
        this.sourceGroupElement = sourceGroupElement;
        this.sliderTransformator = null;
        /** @type {IntersectionObserver|null} loadSlidesWhenNear() of the track */
        this.nearObserver = null;
        /** the Glide of the slides, destroyed before the slider is built again */
        this.glide = null;
        this.itemsPerSlide = 0;
        /** the width of the slider when it was built */
        this.width = null;
        /** whether the visitor paused the slider with its button */
        this.paused = false;
    }

    /**
     * Builds the slider, and builds it again after a resize when the items per slide change.
     * A resize which leaves the width of the slider (the address bar of a phone which slides away
     * changes the height only) changes nothing, and neither does one which leaves the items per
     * slide; a slider built again starts at the slide of the item which was first in view and
     * stays paused when the visitor had paused it. Until the review of #1894 every resize built a
     * new Glide over the old one, which kept its listeners and timers (12 resize listeners on the
     * start page became 52 after five resizes), went back to the first slide and played again.
     *
     * The width is compared before anything is measured: once built, the items sit in a slide of
     * Glide, which is as wide as the rounded offsetWidth of the slider, and measured there they
     * came out a fraction too wide. On a phone the address bar which slid away on the first
     * scroll turned a slider of two squares per slide into one of a single square.
     */
    start() {

        const width = this.sourceGroupElement.getBoundingClientRect().width;
        if (this.glide !== null && width === this.width) {
            return;
        }
        this.width = width;

        this.sliderOptions = SliderStarter._loadOptions(this.sourceGroupElement);

        let sourceElementSelector = this.sliderOptions.getSlidesElementsSelector();
        // a visitor who asked for less motion gets no autoplay and no slide animation; the arrows
        // turn the slides at once (until 7.0.0 the slider moved regardless, A11Y-12)
        const reduceMotion = prefersReducedMotion();
        let autoplay = reduceMotion ? false : this.sliderOptions.getAutoPlay();
        let numberOfItemsPerSlide = ElementSize.calclateNumberOfFittingItemsPerSlide(this.sourceGroupElement, this.sourceGroupElement.querySelector(sourceElementSelector), this.sliderOptions.getNumberOfRowsPerSlide());

        if (this.glide !== null && numberOfItemsPerSlide === this.itemsPerSlide) {
            return;
        }
        let startAt = 0;
        if (this.glide !== null) {
            startAt = Math.floor(this.glide.index * this.itemsPerSlide / numberOfItemsPerSlide);
            this.glide.destroy();
            this.glide = null;
        }
        this.itemsPerSlide = numberOfItemsPerSlide;

        if (null == this.sliderTransformator) {
            this.sliderTransformator = new SliderTransformator(this.sourceGroupElement, sourceElementSelector);
        }
        let rootElement = this.sliderTransformator.transform(numberOfItemsPerSlide);

        if (rootElement === null) {
            return;
        }

        let glide = new Glide(rootElement, {
            type: 'carousel',
            perView: 1,
            gap: 15,
            keyboard: false,
            startAt: startAt,
            autoplay: autoplay,
            animationDuration: reduceMotion ? 0 : 400,
            // on a right-to-left page the slides follow the reading direction, and so do the
            // arrows, whose chevrons the page mirrors
            direction: getComputedStyle(this.sourceGroupElement).direction === 'rtl' ? 'rtl' : 'ltr',
        });

        glide.on(['mount.after', 'run', 'run.after'], () => markSlidesOutOfView(rootElement));
        withImagesHeld(rootElement, () => glide.mount({ Swipe, Controls, Breakpoints, Autoplay, Anchor, Images  }));
        this.glide = glide;

        // a slider started again (a resize) watches its new track only
        if (this.nearObserver) {
            this.nearObserver.disconnect();
        }
        this.nearObserver = loadSlidesWhenNear(rootElement);

        if (autoplay) {
            if (this.paused) {
                glide.pause();
            }
            addPauseButton(rootElement, glide, this.paused, (paused) => { this.paused = paused; });
        }

    }

    static _loadOptions(element) {
        return new SliderOptions(element);
    }


}
